import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * PayPal callback page for handling redirects from PayPal approval flows.
 *
 * This component is used when PayPal redirects back after:
 * - Subscription revision approval (upgrade/downgrade)
 * - Proration payment approval
 *
 * If opened in a popup window (window.opener exists), it will:
 * 1. Show a brief success/error message
 * 2. Tell the parent window to reload
 * 3. Automatically close the popup
 *
 * The parent window will refresh to show the updated subscription.
 */
export default function PayPalCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'success' | 'error' | 'cancelled' | 'loading'>('loading');
  const [message, setMessage] = useState('Processing...');
  const [countdown, setCountdown] = useState(2);

  useEffect(() => {
    // Check URL params for status
    const subscriptionRevision = searchParams.get('subscription_revision');
    const lifetimeApproved = searchParams.get('lifetime');
    const errorMessage = searchParams.get('message');
    const type = searchParams.get('type'); // upgrade, downgrade, free_upgrade

    if (subscriptionRevision === 'success') {
      setStatus('success');
      const typeLabel = type === 'upgrade' ? 'Upgrade' : type === 'downgrade' ? 'Downgrade' : 'Subscription change';
      setMessage(`${typeLabel} completed successfully!`);
    } else if (subscriptionRevision === 'cancelled') {
      setStatus('cancelled');
      setMessage('Subscription change was cancelled.');
    } else if (subscriptionRevision === 'error') {
      setStatus('error');
      setMessage(errorMessage || 'An error occurred during the subscription change.');
    } else if (lifetimeApproved === 'approved') {
      setStatus('success');
      setMessage('Lifetime access activated!');
    } else if (lifetimeApproved === 'cancelled') {
      setStatus('cancelled');
      setMessage('Lifetime purchase was cancelled.');
    } else {
      // Unknown state - treat as success and let page close
      setStatus('success');
      setMessage('Payment processed. Closing...');
    }
  }, [searchParams]);

  // Handle popup close and parent refresh
  useEffect(() => {
    if (status === 'loading') return;

    const isPopup = window.opener !== null;

    if (isPopup) {
      // Immediately tell parent to reload when we're done
      if (status === 'success') {
        try {
          // Reload parent window
          window.opener.location.reload();
        } catch (e) {
          console.error('Could not reload parent window:', e);
        }
      }

      // Start countdown then close popup
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            // Close this popup window
            window.close();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    } else {
      // Not a popup - redirect to subscriptions page after delay
      const timeout = setTimeout(() => {
        window.location.href = '/subscriptions';
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [status]);

  const isPopup = typeof window !== 'undefined' && window.opener !== null;

  const getIcon = () => {
    switch (status) {
      case 'success':
        return <span className="text-5xl text-green-500">✓</span>;
      case 'error':
        return <span className="text-5xl text-red-500">✗</span>;
      case 'cancelled':
        return <span className="text-5xl text-yellow-500">✗</span>;
      default:
        return <span className="text-primary">Loading...</span>;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return 'Success!';
      case 'error':
        return 'Error';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Processing';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="flex justify-center mb-4">
            {getIcon()}
          </div>
          <CardTitle className="text-2xl">{getTitle()}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{message}</p>
          {isPopup && status !== 'loading' && (
            <p className="text-sm text-muted-foreground">
              This window will close in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
          )}
          {!isPopup && status !== 'loading' && (
            <p className="text-sm text-muted-foreground">
              Redirecting to subscriptions...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
